import { useEffect, useState } from "react";
import { api, formatError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function PerformanceReviews() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: "", review_period: "Q1 2026", period_start: "", period_end: "", strengths: "", improvement_areas: "", manager_comments: "", next_review_date: "", status: "submitted" });
  const { user } = useAuth();
  const canReview = ["super_admin","partner","management","business_head","site_head","sales_manager","hr"].includes(user?.role);

  const load = () => api.get("/performance-reviews").then(r => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/users").then(r => setUsers(r.data)).catch(() => {});
  }, []);

  const submit = async () => {
    try { await api.post("/performance-reviews", form); toast.success("Review recorded"); setOpen(false); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="space-y-6 animate-in">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground">HR</div>
          <h1 className="font-serif-display text-3xl">Performance Reviews</h1>
        </div>
        {canReview && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-review-btn"><Plus size={14} className="mr-1"/> New Review</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New Performance Review</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Employee</Label>
                  <Select value={form.employee_id} onValueChange={v => setForm({ ...form, employee_id: v })}>
                    <SelectTrigger data-testid="review-emp-select"><SelectValue placeholder="Select employee"/></SelectTrigger>
                    <SelectContent className="max-h-64 overflow-auto">{users.map(u => <SelectItem key={u.id} value={u.id}>{u.name} — {u.role}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Review Period</Label><Input value={form.review_period} onChange={e => setForm({ ...form, review_period: e.target.value })}/></div>
                <div><Label>Status</Label>
                  <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>{["draft","submitted","reviewed","acknowledged"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Start</Label><Input type="date" value={form.period_start} onChange={e => setForm({ ...form, period_start: e.target.value })}/></div>
                <div><Label>End</Label><Input type="date" value={form.period_end} onChange={e => setForm({ ...form, period_end: e.target.value })}/></div>
                <div className="col-span-2"><Label>Strengths</Label><Textarea value={form.strengths} onChange={e => setForm({ ...form, strengths: e.target.value })}/></div>
                <div className="col-span-2"><Label>Improvement Areas</Label><Textarea value={form.improvement_areas} onChange={e => setForm({ ...form, improvement_areas: e.target.value })}/></div>
                <div className="col-span-2"><Label>Manager Comments</Label><Textarea value={form.manager_comments} onChange={e => setForm({ ...form, manager_comments: e.target.value })}/></div>
                <div><Label>Next Review</Label><Input type="date" value={form.next_review_date} onChange={e => setForm({ ...form, next_review_date: e.target.value })}/></div>
              </div>
              <DialogFooter><Button onClick={submit} data-testid="review-submit">Save Review</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-[11px] uppercase tracking-wider text-muted-foreground">
            <tr><th className="text-left px-4 py-3">When</th><th className="text-left px-4 py-3">Employee</th><th className="text-left px-4 py-3">Period</th><th className="text-left px-4 py-3">Reviewer</th><th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">Next Review</th></tr>
          </thead>
          <tbody>
            {items.map(r => (
              <tr key={r.id} className="border-t border-border">
                <td className="px-4 py-3 text-xs text-muted-foreground">{new Date(r.review_date).toLocaleDateString()}</td>
                <td className="px-4 py-3">{r.employee_name}</td>
                <td className="px-4 py-3 text-xs">{r.review_period}</td>
                <td className="px-4 py-3">{r.reviewer_name}</td>
                <td className="px-4 py-3 capitalize"><span className={`text-[11px] px-2 py-0.5 rounded border ${r.status === "acknowledged" ? "bg-emerald-500/10 text-emerald-700 border-emerald-500/20" : r.status === "reviewed" ? "bg-blue-500/10 text-blue-700 border-blue-500/20" : "bg-secondary border-border"}`}>{r.status}</span></td>
                <td className="px-4 py-3 text-xs">{r.next_review_date || "-"}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan="6" className="px-4 py-10 text-center text-muted-foreground">No reviews yet.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
